import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';
import axios from 'axios';
import API_URL from '../config/API_URL';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const data = await authService.getCurrentUser();
      setUser(data.user);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, access_code) => {
    try {
      // Step 1: Register the user
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, password, access_code },
        { withCredentials: true }
      );
      
      setUser(response.data.user);
      
      // Step 2: Check if user wants premium
      const pendingPremium = localStorage.getItem('pendingPremiumUpgrade');
      
      if (pendingPremium === 'true') {
        localStorage.removeItem('pendingPremiumUpgrade');
        
        // Step 3: Verify we're actually logged in by checking auth status
        try {
          await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        } catch (error) {
          console.error('Session not established, retrying...');
          // Wait a bit and retry
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Step 4: Now create checkout session
        const checkoutResponse = await axios.post(
          `${API_URL}/api/subscription/create-checkout-session`,
          {},
          { withCredentials: true }
        );
        
        return { 
          user: response.data.user, 
          checkoutUrl: checkoutResponse.data.checkout_url 
        };
      }
      
      return { user: response.data.user, checkoutUrl: null };
      
    } catch (error) {
      console.error('Registration error:', error.response?.data || error);
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};